import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowsLeftRight, MagnifyingGlass } from "@phosphor-icons/react";

function fmtDateTime(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * TradeRequestDialog
 * ------------------
 * Post a trade for `tour`. Two modes:
 *
 *   • **Targeted trade** — pick a specific eligible shift; both members
 *     must be cross-eligible. Supervisor still approves.
 *   • **Open trade** — leave the partner blank and post to the Trade
 *     Board; any eligible member can claim it later.
 *
 * Layout: a single 12-column grid; every label, control and helper
 * text lives in its own row so alignment is consistent regardless of
 * viewport width.
 */
export default function TradeRequestDialog({ tour, open, onOpenChange, onRequested }) {
  const [mode, setMode] = useState("open"); // "open" | "targeted"
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open || !tour) return;
    setMode("open");
    setTargetId("");
    setReason("");
    setQ("");
    setCandidates([]);
    setLoading(true);
    api.get(`/tours/${tour.id}/trade-candidates`)
      .then((r) => setCandidates(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load trade candidates"))
      .finally(() => setLoading(false));
  }, [open, tour]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((c) =>
      `${c.assigned_user_name || ""} ${c.position || ""} ${c.required_certification || ""} ${c.ambulance_role || ""}`
        .toLowerCase()
        .includes(needle)
    );
  }, [candidates, q]);

  async function submit() {
    if (mode === "targeted" && !targetId) {
      toast.error("Pick a shift to trade with — or switch to Open Trade");
      return;
    }
    setBusy(true);
    try {
      const payload = { reason };
      if (mode === "targeted") payload.target_tour_id = targetId;
      await api.post(`/tours/${tour.id}/trade-request`, payload);
      toast.success(
        mode === "open"
          ? "Posted to the Trade Board — any eligible member can claim it"
          : "Trade request submitted — supervisors will review"
      );
      onOpenChange(false);
      onRequested && onRequested();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trade request failed");
    } finally { setBusy(false); }
  }

  if (!tour) return null;
  const disableSubmit = busy || (mode === "targeted" && !targetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="trade-dialog">
        <DialogHeader className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ArrowsLeftRight size={18} weight="bold" /> Request Tour Trade
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Post an open trade for anyone to claim, or pick a specific eligible member to swap with.
          </p>
        </DialogHeader>

        {/* ─── Content ───────────────────────────────────────────── */}
        <div className="px-5 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
          {/* Row 1 — the shift being offered */}
          <section>
            <Label className="tab-tag mb-1.5 block">Your Shift</Label>
            <div className="border border-slate-200 rounded-sm p-3 bg-white">
              <div className="font-semibold text-sm">{tour.position || tour.title}</div>
              <div className="text-xs text-slate-600 mono mt-0.5">{fmtDateTime(tour.start_time)}</div>
              <div className="flex items-center gap-1 flex-wrap mt-1.5">
                <Badge className="rounded-sm text-[10px] bg-slate-900 text-white">{tour.required_certification}</Badge>
                {tour.ambulance_role && <Badge className="rounded-sm text-[10px] bg-teal-700 text-white">{tour.ambulance_role}</Badge>}
              </div>
            </div>
          </section>

          {/* Row 2 — mode selector */}
          <section>
            <Label className="tab-tag mb-1.5 block">Trade Type</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("open")}
                className={`text-left border rounded-sm px-3 py-2.5 transition-colors ${mode === "open" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                data-testid="trade-mode-open"
              >
                <div className="font-semibold text-sm">Open Trade</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Post to the Trade Board — anyone eligible can claim.</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("targeted")}
                className={`text-left border rounded-sm px-3 py-2.5 transition-colors ${mode === "targeted" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                data-testid="trade-mode-targeted"
              >
                <div className="font-semibold text-sm">Pick Partner</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Choose a specific shift/member to swap with.</div>
              </button>
            </div>
          </section>

          {/* Row 3 — partner picker (only when targeted) */}
          {mode === "targeted" && (
            <section>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="tab-tag">Trade Partner</Label>
                <span className="text-[10px] text-slate-500 mono">{filtered.length}/{candidates.length} shown</span>
              </div>
              <div className="relative mb-2">
                <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, shift or role"
                  className="rounded-sm h-9 pl-7"
                  data-testid="trade-search"
                />
              </div>
              {loading ? (
                <div className="text-xs text-slate-500 italic p-4 text-center border border-dashed border-slate-200 rounded-sm">Loading eligible shifts…</div>
              ) : filtered.length === 0 ? (
                <div className="text-xs text-slate-600 p-4 text-center border border-dashed border-slate-300 rounded-sm bg-slate-50">
                  No eligible swap partners{candidates.length ? " match that search" : ""}. Trades require another assigned shift where both members can legally cover the other's role and certification.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {filtered.map((c) => (
                    <label
                      key={c.id}
                      className={`grid grid-cols-[auto,1fr] items-center gap-3 p-2.5 border rounded-sm cursor-pointer transition-colors ${targetId === c.id ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                      data-testid={`trade-candidate-${c.id}`}
                    >
                      <input
                        type="radio"
                        name="trade-target"
                        value={c.id}
                        checked={targetId === c.id}
                        onChange={() => setTargetId(c.id)}
                        className="mt-0"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{c.assigned_user_name}</span>
                          <Badge className="rounded-sm text-[10px] bg-slate-900 text-white">{c.required_certification}</Badge>
                          {c.ambulance_role && <Badge className="rounded-sm text-[10px] bg-teal-700 text-white">{c.ambulance_role}</Badge>}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {c.position || c.title} · <span className="mono">{fmtDateTime(c.start_time)}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Row 4 — reason */}
          <section>
            <Label className="tab-tag mb-1.5 block">Reason / Note <span className="text-slate-400 lowercase">— optional</span></Label>
            <Textarea
              className="rounded-sm min-h-[80px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === "open"
                ? "Add context for whoever might claim this trade."
                : "Why you need this trade — visible to supervisors."}
              data-testid="trade-reason"
            />
          </section>
        </div>

        {/* ─── Footer ────────────────────────────────────────────── */}
        <DialogFooter className="px-5 py-3 border-t border-slate-200 gap-2 bg-slate-50">
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="trade-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            disabled={disableSubmit}
            onClick={submit}
            className="rounded-sm bg-slate-900 text-white disabled:opacity-40"
            data-testid="trade-submit"
          >
            {busy ? "Submitting…" : mode === "open" ? "Post Open Trade" : "Submit Trade Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
